import { TipBox, TipWrap, useTip } from "./Tip";

const nf = (x: number) => Intl.NumberFormat("id-ID").format(x ?? 0);

/** Sparkline garis 2px — konteks tren di dalam KPI, tanpa sumbu dan tanpa label.
 *  Bukan chart mandiri: angka besar di sebelahnya yang membawa nilai.
 *  Tetap punya hover: garisnya sendiri hanya menunjukkan bentuk, tidak ada satu pun
 *  angka harian yang terbaca tanpa itu. */
export function Spark({ data, labels, color = "var(--mark-brand)", w = 78, h = 30 }: {
  data: number[]; labels?: string[]; color?: string; w?: number; h?: number;
}) {
  const { wrapRef, tip, tampilDi, sembunyi } = useTip();
  const pts = data.filter((x) => Number.isFinite(x));
  if (pts.length < 2) return <svg width={w} height={h} aria-hidden="true" />;
  const min = Math.min(...pts), max = Math.max(...pts), span = max - min || 1;
  const x = (i: number) => (i / (pts.length - 1)) * (w - 2) + 1;
  const y = (v: number) => h - 3 - ((v - min) / span) * (h - 6);
  const d = pts.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const area = `${d} L${x(pts.length - 1).toFixed(1)},${h} L${x(0).toFixed(1)},${h} Z`;

  const dekat = (clientX: number) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return 0;
    const rel = ((clientX - r.left) / r.width) * w;
    return Math.max(0, Math.min(pts.length - 1, Math.round(((rel - 1) / (w - 2)) * (pts.length - 1))));
  };

  return (
    <TipWrap wrapRef={wrapRef} style={{ display: "inline-block", lineHeight: 0 }}>
      <svg width={w} height={h} style={{ overflow: "visible" }} role="img"
        aria-label={`Tren ${pts.length} titik, puncak ${nf(max)}`}
        onPointerMove={(e) => {
          const i = dekat(e.clientX);
          tampilDi(e.clientX, e.clientY, { judul: labels?.[i] ?? `Titik ke-${i + 1}`, baris: [{ warna: color, label: "nilai", nilai: nf(pts[i]) }] });
        }}
        onPointerLeave={sembunyi}>
        <path d={area} fill={color} opacity={0.08} />
        <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={x(pts.length - 1)} cy={y(pts[pts.length - 1])} r={2.5} fill={color} stroke="var(--color-surface)" strokeWidth={2} />
        {tip && (() => {
          const i = dekat(tip.x + (wrapRef.current?.getBoundingClientRect().left ?? 0));
          return <circle cx={x(i)} cy={y(pts[i])} r={3.5} fill="var(--color-surface)" stroke={color} strokeWidth={2} />;
        })()}
      </svg>
      <TipBox tip={tip} />
    </TipWrap>
  );
}
